import { DateTime, Duration, Interval } from "luxon";
import { ElementOf } from "../types/typeHelpers";
import { Duration as ProtoDuration } from "@bufbuild/protobuf";

export const DATETIME_FORMAT = "ccc dd-MM HH:mm:ss";
export const DATETIME_FORMAT_NO_TIME = "cccc dd LLLL";
export const DATETIME_FORMAT_NO_SECONDS = "ccc dd-MM HH:mm";
export const DATETIME_FORMAT_NO_DATE = "ccc HH:mm:ss";
export const DATETIME_FORMAT_NO_DATE_NO_SECONDS = "ccc HH:mm";
export const DURATION_FORMAT = "hh:mm:ss";
export const DURATION_FORMAT_NO_SECONDS = "hh:mm";
export const TIME_FORMAT = "HH:mm:ss";

export interface DateOnly {
  year: number;
  month: number;
  day: number;
}

interface WithStartAndEndTime {
  startTime: DateTime;
  endTime: DateTime;
}

export function hasPositiveDuration({ startTime, endTime }: WithStartAndEndTime): boolean {
  return endTime.diff(startTime).toMillis() > 0;
}

export function combineDateAndTime(
  dateString: string,
  timeString: string,
): DateTime<true> | DateTime<false> {
  return DateTime.fromISO(`${dateString}T${timeString}`);
}

const orderOfUnits = ["millisecond", "second", "minute", "hour", "day", "month", "year"] as const;
type TimeUnit = ElementOf<typeof orderOfUnits>;
type DurationLike = Partial<Record<TimeUnit, number>>;

function resetSmallerUnits(thanUnit: TimeUnit): DurationLike {
  const resetSmallerTimeUnits: DurationLike = {};
  for (let i = 0; i < orderOfUnits.indexOf(thanUnit); i++) {
    resetSmallerTimeUnits[orderOfUnits[i]] = 0;
  }
  return resetSmallerTimeUnits;
}

export function roundDown(dateTime: DateTime, unit: TimeUnit, wholeInterval: number): DateTime {
  return dateTime
    .set({ [unit]: Math.floor(dateTime.get(unit) / wholeInterval) * wholeInterval })
    .set(resetSmallerUnits(unit));
}

export function roundUp(dateTime: DateTime, unit: TimeUnit, wholeInterval: number): DateTime {
  return dateTime
    .set({ [unit]: Math.ceil(dateTime.get(unit) / wholeInterval) * wholeInterval })
    .set(resetSmallerUnits(unit));
}

/**
 * Formats a Luxon Duration into a string according to a specified format string.
 * The format string may contain the following special tokens, which are replaced by
 * days, hours, minutes, and seconds using modulus operations where applicable: '%d', '%h', '%m', '%s'.
 * - If the token is lowercase, it is replaced by the value followed by its unit character (d, u, ', s).
 * - If the token is uppercase, it adds a leading zero if the value is less than 10.
 *
 * The hours calculation will include a modulus operation (% 24) only if the format string includes '%d' or '%D'.
 * If the totalDays is 0, the days part will be omitted from the formatted string.
 *
 * @param duration - The Luxon Duration to be formatted.
 * @param formatString - The format string that indicates how to format the duration.
 *
 * @returns The formatted string representation of the Duration.
 */
export function formatDuration(duration: Duration, formatString: string): string {
  const totalDays = Math.floor(duration.as("days"));

  // Check if the format string includes day tokens
  const useModulusForHours = formatString.includes("%d") || formatString.includes("%D");
  const totalHours = useModulusForHours
    ? Math.floor(duration.as("hours") % 24)
    : Math.floor(duration.as("hours"));
  const totalMinutes = Math.floor(duration.as("minutes") % 60);
  const totalSeconds = Math.floor(duration.as("seconds") % 60);

  const tokenValues: { [key: string]: number } = {
    "%d": totalDays,
    "%h": totalHours,
    "%m": totalMinutes,
    "%s": totalSeconds,
  };

  const suffix: { [key: string]: string } = { "%d": "d", "%h": "u", "%m": "'", "%s": "s" };

  return formatString
    .replace(/(%[dhmsDHMS])/g, (match) => {
      const token = match.toLowerCase() as "%d" | "%h" | "%m" | "%s";
      const value = tokenValues[token];
      const unit = suffix[token];
      const isUppercase = match === match.toUpperCase();
      const formattedValue = isUppercase ? padWithZero(value) : value;

      // Skip the days part if totalDays is 0
      if (token === "%d" && totalDays === 0) {
        return "";
      }

      return `${formattedValue}${unit}`;
    })
    .trim();
}

export function getMinMaxDateTimes(dateTimes: DateTime[]): { min: DateTime; max: DateTime } {
  if (dateTimes.length === 0) {
    throw new Error("Array cannot be empty");
  }

  const min = dateTimes.reduce((a, b) => (a < b ? a : b), dateTimes[0]);
  const max = dateTimes.reduce((a, b) => (a > b ? a : b), dateTimes[0]);

  return { min, max };
}

export function generateDateArray(min: DateTime, max: DateTime): DateTime[] {
  const dateArray: DateTime[] = [];
  let currentDate = min.startOf("day");

  while (currentDate <= max.startOf("day")) {
    dateArray.push(currentDate);
    currentDate = currentDate.plus({ days: 1 });
  }

  return dateArray;
}

export function durationToProto(luxonDuration: Duration): ProtoDuration {
  return new ProtoDuration({ seconds: BigInt(luxonDuration.as("seconds")) });
}

export function dateTimeToMinute(dt: DateTime): string {
  return dt.minute.toString();
}

export function dateTimeToTimeWithPint(dt: DateTime<true>): string {
  const hours = dt.hour;
  const minutes = dt.minute;
  const seconds = dt.second;
  const tenth = Math.floor(seconds / 6);
  return `${padWithZero(hours)}:${padWithZero(minutes)}.${tenth}`;
}

export function dateTimeToMinuteWithPint(dt: DateTime): string {
  const minutes = dt.minute;
  const seconds = dt.second;
  const tenth = Math.floor(seconds / 6);
  return `${minutes}.${tenth}`;
}

export function parseTimeInPint(text: string, date: DateTime<true>): DateTime<true> | undefined {
  const timeRegex = /^(?<hours>(([0-1])?\d)|2[0-3]):(?<minutes>[0-5]\d).(?<pint>\d)$/;
  const groups = timeRegex.exec(text)?.groups;
  if (!groups) {
    return;
  }

  const datetime = DateTime.local(
    date.year,
    date.month,
    date.day,
    Number.parseInt(groups.hours),
    Number.parseInt(groups.minutes),
    Number.parseInt(groups.pint) * 6,
  );

  if (datetime.isValid) {
    return datetime;
  }
}

export const getDatesFromInterval = (interval: Interval<true>) =>
  Interval.fromDateTimes(interval.start.startOf("day"), interval.end.endOf("day"))
    .splitBy({ day: 1 })
    .map((d) => d.start!);

const padWithZero = (num: number) => (num < 10 ? `0${num}` : `${num}`);
